import moment from 'moment';

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

export const isUsableTimestamp = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

export function formatTimestamp(value: unknown): string | null {
  if (!isUsableTimestamp(value)) return null;
  const at = moment(value);
  return at.isValid() ? at.format('D MMM YYYY [at] h:mm A') : null;
}

export function formatClockTime(value: unknown): string | null {
  if (!isUsableTimestamp(value)) return null;
  const at = moment(value);
  return at.isValid() ? at.format('h:mm A') : null;
}

export function formatShortDuration(milliseconds: unknown): string | null {
  if (typeof milliseconds !== 'number' || !Number.isFinite(milliseconds) || milliseconds < 0) {
    return null;
  }

  if (milliseconds < MINUTE_MS) return '<1 m';
  if (milliseconds < HOUR_MS) return `${Math.floor(milliseconds / MINUTE_MS)} m`;

  if (milliseconds < DAY_MS) {
    const hours = Math.floor(milliseconds / HOUR_MS);
    const minutes = Math.floor((milliseconds % HOUR_MS) / MINUTE_MS);
    return minutes ? `${hours} h ${minutes} m` : `${hours} h`;
  }

  const days = Math.floor(milliseconds / DAY_MS);
  const hours = Math.floor((milliseconds % DAY_MS) / HOUR_MS);
  return hours ? `${days} d ${hours} h` : `${days} d`;
}

export function formatShortElapsed(since: unknown, now: number = Date.now()): string | null {
  if (!isUsableTimestamp(since)) return null;
  return formatShortDuration(now - since);
}

const plural = (count: number, unit: string) => `${count} ${unit}${count === 1 ? '' : 's'}`;

export function formatDuration(milliseconds: unknown): string | null {
  if (typeof milliseconds !== 'number' || !Number.isFinite(milliseconds) || milliseconds < 0) {
    return null;
  }

  if (milliseconds < MINUTE_MS) return 'less than a minute';

  if (milliseconds < HOUR_MS) {
    return plural(Math.floor(milliseconds / MINUTE_MS), 'minute');
  }

  if (milliseconds < DAY_MS) {
    const hours = Math.floor(milliseconds / HOUR_MS);
    const minutes = Math.floor((milliseconds % HOUR_MS) / MINUTE_MS);
    return minutes
      ? `${plural(hours, 'hour')} ${plural(minutes, 'minute')}`
      : plural(hours, 'hour');
  }

  const days = Math.floor(milliseconds / DAY_MS);
  const hours = Math.floor((milliseconds % DAY_MS) / HOUR_MS);
  return hours ? `${plural(days, 'day')} ${plural(hours, 'hour')}` : plural(days, 'day');
}

export function formatElapsed(since: unknown, now: number = Date.now()): string | null {
  if (!isUsableTimestamp(since)) return null;
  return formatDuration(now - since);
}

export function formatLastActivation(
  window: { startedAt: number; endedAt: number } | null | undefined
): string | null {
  if (!window) return null;

  const startedAt = formatTimestamp(window.startedAt);
  if (!startedAt) return null;

  if (!isUsableTimestamp(window.endedAt)) return null;
  const duration = formatDuration(window.endedAt - window.startedAt);
  if (!duration) return null;

  return `${startedAt} · lasted ${duration}`;
}
